import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";
import { Options } from "@angular-slider/ngx-slider";
import { FormControl } from "@angular/forms";
import { FilterProjectService } from "../../../../services/internal-services/filter-project.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../../shared/model/google-analytics-constants";
import { FeatureFlagsService } from "../../../../services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../../../model/feature-flags";
import { IProjectAOI } from "../../../../interfaces/i-project-aoi";
import { GoogleAnalyticsService } from "../../../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../../../services/internal-services/viewer-services/inject-base-viewer.service";

@Directive()
export abstract class BaseTimelineComponent implements OnDestroy, AfterViewInit {
  viewerService = inject(VIEWER_SERVICE);

  _aois: IProjectAOI[] = [];

  aoiPosition = [];

  fixedDatesTimes = [];

  epsilon = 0.025;

  hoverValue = null;

  manualRefresh: EventEmitter<void> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];

  sliderControl: FormControl = new FormControl(0);

  showFilters = false;

  showSlider = false;

  showMiniSlider = false;

  showSwipeTooltip = false;

  options: Options = {
    floor: 0,
    ceil: 0,
  };

  FeatureFlags = FeatureFlags;

  protected readonly debounceTimeMs = 300;

  @Input()
  public enableContrast: boolean = false;

  @Input()
  public timelineBlocked: boolean = false;

  @Input()
  public enabledDebug: boolean = false;

  @Input()
  public enabledFilters: boolean = true;

  @Input()
  set swipeStatus(status: any) {
    if (this.viewerService?.getProjectStatus()?.mapStatus.swipeStatus.enable) {
      this.showSwipeTooltip = this.viewerService.getProjectStatus().mapStatus.swipeStatus.enable;
    }
  }

  @ViewChild("slider") sliderRef: any;

  @ViewChild("sliderDiv") sliderDivRef: any;

  @ViewChild("dateDiv") dateDivRef: any;

  @ViewChild("layerName") layerNameRef: any;

  @ViewChild("circle") circleRef: any;

  public timelineTooltip = "";

  private sliderChanges: MutationObserver = new MutationObserver(() => {
    this.setStopsElementClass();
  });

  protected constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    public cdr: ChangeDetectorRef,
    public flagService: FeatureFlagsService,
  ) {}

  ngOnDestroy() {
    if (this.sliderChanges) {
      this.sliderChanges.disconnect();
    }
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  ngAfterViewInit(): void {
    if (this.sliderRef)
      this.sliderChanges.observe(this.sliderRef.ticksElement.elemRef.nativeElement, {
        childList: true,
        subtree: true,
      });
    this.subscriptions.push(
      this.viewerService.enableContrast$.subscribe((enable) => {
        this.enableContrast = enable;
        this.timelineTooltip = enable ? "The Timeline is not available while using Gray Sky Contrast" : "";
        this.updateSliderOptions();
        this.manualRefresh.emit();
      }),
    );
    this.updateSliderValue();
  }

  getDate(aoi: IProjectAOI): Date {
    if (this.viewerService.is3DMode()) {
      return aoi.getNewestImage(this.viewerService.orientationSelected)?.capturedDate || null;
    }
    return aoi.nadirDate;
  }

  /**
   * Precalculating fixed dates to avoid overlapping
   */
  preCalculateFixedDates(aois: IProjectAOI[]): void {
    this.fixedDatesTimes = [];
    if (aois?.length) {
      // Calculate init difference between aois
      let deltasDates = aois.map((aoi, index) => {
        this.fixedDatesTimes[index] = this.getDate(aoi)?.getTime();
        if (index > 0) {
          return this.fixedDatesTimes[index] - this.fixedDatesTimes[index - 1];
        }
        return null;
      });
      // remove first item (null) from deltasDates
      deltasDates.shift();

      // Calculating minimum space between stops in percentage
      let eps = (this.fixedDatesTimes[this._aois.length - 1] - this.fixedDatesTimes[0]) * 0.05;
      if (eps === 0) eps = 1;
      aois.forEach((aoi, index) => {
        if (index > 0) {
          const deltaTimeAois = deltasDates[index - 1];
          // If small space
          if (deltaTimeAois < eps) {
            // Getting max space
            let maxDelta = 0;
            let maxDeltaIndex = null;
            deltasDates.forEach((d, i) => {
              maxDeltaIndex = d > maxDelta ? i : maxDeltaIndex;
              maxDelta = d > maxDelta ? d : maxDelta;
            });

            if (maxDelta > eps) {
              // Move dates to left side
              if (maxDeltaIndex < index - 1) {
                // Fixing dates
                for (let i = maxDeltaIndex + 1; i < index; i++) {
                  this.fixedDatesTimes[i] -= eps - deltaTimeAois;
                }

                // Move dates to right side
              } else if (maxDeltaIndex > index - 1) {
                // Fixing dates
                for (let i = index; i <= maxDeltaIndex; i++) {
                  this.fixedDatesTimes[i] += eps - deltaTimeAois;
                }
              }
              // Updating space array
              deltasDates = deltasDates.map((d) => {
                // eslint-disable-next-line no-param-reassign
                if (d === maxDelta) d -= eps - deltaTimeAois;
                return d;
              });
              deltasDates[index - 1] = deltaTimeAois + (eps - deltaTimeAois);
            } else {
              // if not space enough
              deltasDates[index - 1] = deltaTimeAois;
              this.fixedDatesTimes[index] = this.getDate(aoi)?.getTime() + eps;
            }
          }
        } else {
          // Saving date
          this.fixedDatesTimes[index] = this.getDate(aoi)?.getTime();
        }
      });
    }
  }

  /**
   * Create options and update values when get projects on new collection
   * On change mode
   */
  createSliderOptions() {
    if (this.showSlider) {
      this.updateSliderOptions();
    }
  }

  /**
   * Method to handle how the legend, ticks and values are shown
   */
  updateSliderOptions(): void {
    const ceil = this._aois ? this._aois.length - 1 : -1;
    this.options = {
      disabled: this.enableContrast,
      animate: false,
      showTicks: true,
      showTicksValues: true,
      floor: 0,
      showOuterSelectionBars: false,
      ceil: ceil,
      getLegend: this.getLegend.bind(this),
      ticksTooltip: this.getTicksTooltip.bind(this),
      getTickColor: this.getTickColor.bind(this),
      customValueToPosition: this.getCustomValuePosition.bind(this),
      customPositionToValue: this.getCustomPositionValue.bind(this),
    };

    this.cdr.detectChanges();
  }

  /**
   * Setting stop classes to hover functionality
   */
  setStopsElementClass() {
    const stopsElement = this.sliderRef?.ticksElement.elemRef.nativeElement;
    this._aois?.forEach((aoi, index) => {
      const tick = stopsElement?.children[index];
      if (tick) {
        this.handleTickClass(tick, index, aoi);
      }
    });
  }

  handleTickClass(tick: any, index: number, aoi: IProjectAOI) {
    if (tick) {
      if (aoi.isGraySky()) {
        if (!tick.classList.contains("gray")) {
          tick.classList.add("gray");
        }
      } else if (aoi.isWide()) {
        if (!tick.classList.contains("wide")) {
          tick.classList.add("wide");
        }
      } else {
        tick.classList.remove("gray");
        tick.classList.remove("wide");
      }
      const tickClassText = aoi.isAOIOblique() ? "threeD" : "twoD";
      if (!tick.classList.contains(tickClassText)) {
        tick.classList.remove(aoi.isAOIOblique() ? "twoD" : "threeD");
        tick.classList.add(tickClassText);
      }
      tick.onmouseenter = () => {
        if (index !== this.sliderControl.value) {
          this.hoverValue = index;
          this.manualRefresh.emit();
        }
      };
      tick.onmouseleave = () => {
        this.hoverValue = this.hoverValue === index ? null : this.hoverValue;
        this.manualRefresh.emit();
      };
    }
  }

  showProjectDetails(value: number): boolean {
    if (!this._aois[value]) return false;
    return (
      this._aois[value].isVisible() &&
      ((this.sliderControl.value === value &&
        (!this.hoverValue ||
          Math.abs(this.aoiPosition[value] - this.aoiPosition[this.hoverValue]) > 0.08 ||
          !this._aois[this.hoverValue].isVisible()
        )) ||
        this.hoverValue === value) &&
      !this.timelineBlocked
    );
  }

  showProjectYear(value: number): boolean {
    if (!this._aois[value]) return false;
    return value === 0 || (value > 0 && this._aois[value].getYear() !== this._aois[value - 1].getYear());
  }

  getTickColor(value: number): string {
    const tick = this.sliderRef.ticksElement.elemRef.nativeElement.children[value];
    const aoi = this._aois ? this._aois[value] : null;
    if (!aoi || !tick) {
      return "rgba(255, 255, 255, 0.25)";
    }

    if (aoi.isVisible() && tick.classList.contains("collection-hide")) {
      tick.classList.remove("collection-hide");
    } else if (!aoi.isVisible() && !tick.classList.contains("collection-hide")) {
      tick.classList.add("collection-hide");
    }

    // Class to hide storm icon when selected
    if (this.sliderControl.value === value) {
      tick.classList.add("selected");
    } else {
      tick.classList.remove("selected");
    }

    this.handleTickClass(tick, value, aoi);

    return aoi.isVisible() ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.25)";
  }

  getTicksTooltip(value: number): string {
    return this._aois && this._aois[value] && !this._aois[value].isVisible() ? "Click to activate" : "";
  }

  getCustomValuePosition(val: number, minVal: number, maxVal: number): number {
    // Calculate position
    const value = this.fixedDatesTimes[val];
    const minValue = this.fixedDatesTimes[minVal];
    const maxValue = this.fixedDatesTimes[maxVal];
    const range: number = maxValue - minValue;
    const position = range === 0 ? 0 : (value - minValue) / range;

    // Saving current position
    this.aoiPosition[val] = position;

    return position;
  }

  getCustomPositionValue(percent: number): number {
    let value = this.sliderControl.value;
    this.aoiPosition.forEach((p, index) => {
      if (Math.abs(p - percent) < this.epsilon) {
        value = index;
      }
    });
    return value;
  }

  /**
   * Apply filter configuration to collection and aois visibility
   * First change visibility for layers
   * Then update layers/project status like center aoi blabla
   * Also we have to control de 3d mode
   * Then force change layers event
   * @param event
   */
  applyTimelineFilter(event: any) {
    if (event) {
      this.viewerService.applyTimelineFilter(event.filters, event.projects?.visible, event.projects?.hidden);
      this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
    }

    this.manualRefresh.emit();
  }

  onChangeProject(value: number): void {
    const aoi = this._aois[value];

    this.updateFiltersByAOI(aoi);

    //maybe we can do just this
    this.viewerService.changeProject(aoi);
    this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
    this.sliderControl.reset(value);

    this.sendAnalyticsEvent(aoi);
  }

  /**
   * Update the filters after selecting the aoi in order to activate the filters that the selected aoi has,
   * for example: if the aoi it´s bluesky and the filter bluesky it´s deactivated we activate the filter.
   * @param aoi
   */
  updateFiltersByAOI(aoi: IProjectAOI): void {
    let filter = this.viewerService.getTimelineFilters();

    //if not match the project with the current filter we should change the filter and recalculate filters
    if (aoi && !FilterProjectService.matchWithFilter(filter, aoi)) {
      filter = FilterProjectService.updateFilterByProject(filter, aoi);
      const projects = this.viewerService.filterProjectsByFilter(filter);
      this.viewerService.applyTimelineFilter(filter, projects?.visible, projects?.hidden);
      FilterProjectService.processAfterFilter(projects?.visible, filter);
    }
  }

  /**
   * Send the analytics events
   * @param aoi
   */
  sendAnalyticsEvent(aoi: IProjectAOI): void {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.TIMELINE_CHANGE_AOI,
      GoogleAnalyticsConstantsCategory.TIMELINE,
      true,
    );
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      aoi.isGraySky()
        ? GoogleAnalyticsConstantsEvents.TIMELINE_AOI_GRAY_SKY
        : GoogleAnalyticsConstantsEvents.TIMELINE_AOI_BLUE_SKY,
      GoogleAnalyticsConstantsCategory.TIMELINE,
      true,
    );
  }

  abstract get aois(): IProjectAOI[];

  abstract set aois(aois: IProjectAOI[]);

  abstract updateSliderValue();

  abstract getLegend(value: number): string;
}
